/**
 * Stickers/GIFs — SPEC §1.2 (minimal metadata), §2.3, NFR-PWA-002.
 * Local-only model: bundled sticker packs (same-origin SVG art) and
 * user-imported GIF/WebP/PNG files travel inside the E2EE envelope.
 * No third-party sticker/GIF providers — search terms and fetches never
 * leave the device, so no metadata is leaked to a remote catalog.
 */

export const STICKER_GIF_FEATURE_STATUS = {
  availableInUi: true,
  provider: "local-only",
  /** Bundled packs render locally on both ends; only a tiny id reference is sealed (§9.5). */
  builtinPackTransport: "inline-reference",
  maxCustomStickerBytes: 64 * 1024,
  maxGifBytes: 25 * 1024 * 1024,
  gateMessage:
    "Local stickers and GIFs only — encrypted end to end. No third-party sticker providers (privacy).",
} as const;

/* Built-in sticker wire helpers (§9.5 inline budget — tiny reference, not bytes). */

const STICKER_LABEL_PREFIX = "🏷️";

export function stickerWireBody(packId: string, stickerId: string): string {
  return JSON.stringify({ op: "sticker", kind: "sticker", pack: packId, id: stickerId });
}

/** Stable message label: `🏷️ pack/id`. Consumer apps render from their local pack. */
export function stickerRefLabel(packId: string, stickerId: string): string {
  return `${STICKER_LABEL_PREFIX} ${packId}/${stickerId}`;
}

export function parseStickerRef(body: string): { pack: string; id: string } | null {
  if (!body.startsWith(`${STICKER_LABEL_PREFIX} `)) return null;
  const ref = body.slice(STICKER_LABEL_PREFIX.length + 1).trim();
  // Strict: dotted sub-domains allowed, but never leading dots / traversal shapes.
  const m = /^([a-z0-9](?:[a-z0-9.-]*[a-z0-9])?)\/([a-z0-9-]+)$/.exec(ref);
  return m ? { pack: m[1]!, id: m[2]! } : null;
}

/** Label prefixes for sticker/GIF *file* attachments (rendered from E2EE bytes). */
export const STICKER_FILE_LABEL_PREFIX = STICKER_LABEL_PREFIX;
export const GIF_FILE_LABEL_PREFIX = "🎞️";
